import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/** Step 1 — Basic Info / Consent */
export interface Step1FormValues {
  product: string;
  applicantType: string;
  firstName: string;
  lastName: string;
  phone_number: string;
  email: string;
  pincode: string;
  terms_conditions: boolean;
  privacy_policy: boolean;
  isMobileVerified: boolean;
  isEmailVerified: boolean;
}

export interface DropdownOptionValue {
  value: string;
  label: string;
  code?: string;
}

export interface Step2FormValues {
  courseCategory: string;
  admissionStatus: string;

  country: DropdownOptionValue | string;
  universityCountryId: string;
  universityCampusId: string;

  university: DropdownOptionValue | string;
  universityNotInList: boolean;

  institute_code: string | null;
  institute: DropdownOptionValue | string;
  instituteNotInList: boolean;

  courseCode: string | null;
  courseName: string;
  courseNameNotInList: boolean;

  courseType: string;

  countryLabel: string;
  universityLabel: string;
  instituteLabel: string;
  courseNameLabel: string;

  selectedCountryId: string;
  selectedUniversityId: string;
  selectedInstituteId: string;

  notInListUniversity: boolean;
  notInListInstitute: boolean;
  notInListCourseName: boolean;
}

/** Step 3 — Loan Details */
export interface Step3FormValues {
  loanAmount: number | null;
  totalCost: number | null;
}

export interface EligibilityCheckState {
  currentStep: number;
  stepFields: {
    basicDetails: Step1FormValues;
    courseDetails: Step2FormValues;
    loanDetails: Step3FormValues;
  };
  trackingId: string | null;
  encryptionKey: string | null;
  isSuccessEligible: boolean;
  isStepsCompleted: boolean;
  breDetails: any;
}

const INITIAL_STEP1: Partial<Step1FormValues> = {
  product: "SLI",
  applicantType: "Student",
  firstName: "",
  lastName: "",
  phone_number: "",
  email: "",
  pincode: "",
  terms_conditions: false,
  privacy_policy: false,
  isMobileVerified: false,
  isEmailVerified: false,
};

const INITIAL_STEP2: Partial<Step2FormValues> = {
  courseCategory: "",
  admissionStatus: "",
  country: "",
  universityCountryId: "",
  universityCampusId: "",
  university: "",
  universityNotInList: false,
  institute_code: null,
  institute: "",
  instituteNotInList: false,
  courseCode: null,
  courseName: "",
  courseNameNotInList: false,
  courseType: "",
  countryLabel: "",
  universityLabel: "",
  instituteLabel: "",
  courseNameLabel: "",
  selectedCountryId: "",
  selectedUniversityId: "",
  selectedInstituteId: "",
  notInListUniversity: false,
  notInListInstitute: false,
  notInListCourseName: false,
};

const INITIAL_STEP3: Partial<Step3FormValues> = {
  loanAmount: null,
  totalCost: null,
};

const initialState: EligibilityCheckState = {
  currentStep: 1,
  stepFields: {
    basicDetails: INITIAL_STEP1 as Step1FormValues,
    courseDetails: INITIAL_STEP2 as Step2FormValues,
    loanDetails: INITIAL_STEP3 as Step3FormValues,
  },
  trackingId: null,
  encryptionKey: null,
  isSuccessEligible: false,
  isStepsCompleted: false,
  breDetails: {},
};

const eligibilityCheckSlice = createSlice({
  name: "eligibilityCheck",
  initialState,
  reducers: {
    setBasicDetails(state, action: PayloadAction<Step1FormValues>) {
      state.stepFields.basicDetails = action.payload;
    },
    setCourseDetails(state, action: PayloadAction<Step2FormValues>) {
      state.stepFields.courseDetails = action.payload;
    },
    setLoanDetails(state, action: PayloadAction<Step3FormValues>) {
      state.stepFields.loanDetails = action.payload;
    },

    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },

    setCurrentTrackingId(state, action: PayloadAction<string>) {
      state.trackingId = action.payload;
    },
    setEncryptionKey(state, action: PayloadAction<string>) {
      state.encryptionKey = action.payload;
    },
    setIsSuccessEligible(state, action: PayloadAction<boolean>) {
      state.isSuccessEligible = action.payload;
    },
    setIsStepsCompleted(state, action: PayloadAction<boolean>) {
      state.isStepsCompleted = action.payload;
    },
    setBreDetails(state, action: PayloadAction<boolean>) {
      state.breDetails = action.payload;
    },
    resetAll() {
      return { ...initialState };
    },
  },
});

export const {
  setBasicDetails,
  setCourseDetails,
  setLoanDetails,
  setCurrentStep,
  setCurrentTrackingId,
  setEncryptionKey,
  setIsSuccessEligible,
  setIsStepsCompleted,
  setBreDetails,
  resetAll,
} = eligibilityCheckSlice.actions;

export default eligibilityCheckSlice.reducer;
